import React from 'react';
import { renderDigestMarkdown } from '../utils/markdown';
import { formatDateCompact } from '../utils/dateUtils';
import { ArticleLink } from '../components/SmartLink';

const TopicDigestSection = ({
  topic,
  digest,
  digestLoading,
  digestExpanded,
  setDigestExpanded,
  markingRead,
  onMarkAllRead,
  onFetchDigest,
  onHighlightDigestItem,
  isDigestItemHighlighted,
  highlightingItem,
  digestHighlights,
  articleNavContext = {},
  articleNavPageState = {},
  whatsNewRef,
  coreThemeRef,
  recommendedReadsRef,
}) => {
  if (!topic || topic.unread_count <= 0) return null;

  return (
    <div className="card-clay !p-0 mb-6 overflow-hidden">
      {!digest && !digestExpanded ? (
        <button
          onClick={onFetchDigest}
          disabled={digestLoading}
          className="w-full px-3 py-4 sm:p-6 text-left hover:bg-clay-bg transition-colors rounded-clay"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-secondary-100 rounded-clay">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-heading">AI Digest</h3>
                <p className="text-sm text-secondary-text">Get a summary of unread articles in this topic</p>
              </div>
            </div>
            {digestLoading ? (
              <div className="spinner-fun"></div>
            ) : (
              <span className="text-primary-400">▶</span>
            )}
          </div>
        </button>
      ) : digest && (
        <div className="px-3 py-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-secondary-100 rounded-clay flex-shrink-0">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-heading">AI Digest</h3>
                <p className="text-xs text-secondary-text">
                  {digest.article_count} articles • {formatDateCompact(digest.generated_at)}
                  {topic.unread_count > 0 && topic.unread_count < digest.article_count && !markingRead && (
                    <span className="text-tertiary-text opacity-60">
                      {' '}· {digest.article_count - topic.unread_count} already read
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={onMarkAllRead}
                disabled={markingRead || topic.unread_count === 0}
                className="btn-clay btn-clay-sm bg-fun-mint hover:bg-emerald-400 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingRead ? 'Marking...' : `✓ Mark All Read`}
              </button>
              <button
                onClick={() => setDigestExpanded(!digestExpanded)}
                className="btn-clay-ghost btn-clay-sm"
              >
                {digestExpanded ? '▼' : '▶'}
              </button>
            </div>
          </div>

          {digestExpanded && (
            <div className="space-y-6">
              {/* What's New */}
              <div ref={whatsNewRef}>
                <h4 className="font-display text-md font-semibold text-heading mb-3 flex items-center">
                  <span className="text-lg mr-2">📰</span>
                  What&apos;s New
                </h4>
                <ul className="space-y-3">
                  {digest.whats_new.map((item, idx) => {
                    const text = typeof item === 'string' ? item : item.text;
                    const articleId = typeof item === 'object' ? item.article_id : null;
                    const isHighlighted = isDigestItemHighlighted(text);
                    const itemKey = `whats_new-${text}`;
                    const isHighlighting = highlightingItem === itemKey;

                    return (
                      <li
                        key={idx}
                        className={`group relative text-base text-body-text leading-relaxed pl-2 sm:pl-4 border-l-3 ${
                          isHighlighted ? 'border-fun-lemon bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r-clay' : 'border-fun-sky'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">{renderDigestMarkdown(text, articleId, articleNavContext)}</div>
                          <button
                            onClick={() => onHighlightDigestItem(text, 'whats_new', articleId)}
                            disabled={isHighlighted || isHighlighting}
                            className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded-clay transition-all duration-200 ${
                              isHighlighted
                                ? 'text-fun-peach bg-amber-100 dark:bg-amber-900/40 cursor-default'
                                : 'text-tertiary-text hover:text-fun-peach hover:bg-amber-50 dark:hover:bg-amber-900/30 opacity-0 group-hover:opacity-100'
                            }`}
                            title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                          >
                            {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Core Theme */}
              <div ref={coreThemeRef}>
                <h4 className="font-display text-md font-semibold text-heading mb-3 flex items-center">
                  <span className="text-lg mr-2">🎯</span>
                  Core Theme
                </h4>
                <ul className="space-y-3">
                  {digest.core_theme.map((item, idx) => {
                    const isHighlighted = isDigestItemHighlighted(item);
                    const itemKey = `core_theme-${item}`;
                    const isHighlighting = highlightingItem === itemKey;

                    return (
                      <li
                        key={idx}
                        className={`group relative text-base text-body-text leading-relaxed pl-2 sm:pl-4 border-l-3 ${
                          isHighlighted ? 'border-fun-lemon bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r-clay' : 'border-secondary-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">{renderDigestMarkdown(item)}</div>
                          <button
                            onClick={() => onHighlightDigestItem(item, 'core_theme')}
                            disabled={isHighlighted || isHighlighting}
                            className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded-clay transition-all duration-200 ${
                              isHighlighted
                                ? 'text-fun-peach bg-amber-100 dark:bg-amber-900/40 cursor-default'
                                : 'text-tertiary-text hover:text-fun-peach hover:bg-amber-50 dark:hover:bg-amber-900/30 opacity-0 group-hover:opacity-100'
                            }`}
                            title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                          >
                            {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Recommended Reads */}
              {digest.recommended_reads && digest.recommended_reads.length > 0 && (
                <div ref={recommendedReadsRef}>
                  <h4 className="font-display text-md font-semibold text-heading mb-3 flex items-center">
                    <span className="text-lg mr-2">⭐</span>
                    Recommended Reads
                  </h4>
                  <div className="space-y-3">
                    {digest.recommended_reads.map((read, idx) => {
                      const isHighlighted = isDigestItemHighlighted(read.reason);
                      const itemKey = `recommended_reads-${read.reason}`;
                      const isHighlighting = highlightingItem === itemKey;

                      return (
                        <ArticleLink
                          key={idx}
                          articleId={read.article_id}
                          context={articleNavContext}
                          pageState={articleNavPageState}
                          className={`group block p-2 sm:p-4 rounded-clay border-2 transition-all duration-200 cursor-pointer no-underline ${
                            isHighlighted
                              ? 'bg-yellow-50 dark:bg-yellow-950/30 border-fun-lemon hover:border-fun-peach shadow-clay-sm'
                              : 'bg-accent-50 dark:bg-accent-950/30 border-accent-200 dark:border-accent-800 hover:border-accent-300 dark:hover:border-accent-700 hover:shadow-clay-sm'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-medium text-heading text-base flex-1">{read.title}</div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onHighlightDigestItem(read.reason, 'recommended_reads');
                              }}
                              disabled={isHighlighted || isHighlighting}
                              className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded-clay transition-all duration-200 ${
                                isHighlighted
                                  ? 'text-fun-peach bg-amber-100 dark:bg-amber-900/40 cursor-default'
                                  : 'text-tertiary-text hover:text-fun-peach hover:bg-amber-50 dark:hover:bg-amber-900/30 opacity-0 group-hover:opacity-100'
                              }`}
                              title={isHighlighted ? 'Already highlighted' : 'Highlight this reason'}
                            >
                              {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                            </button>
                          </div>
                          <div className="text-base text-secondary-text italic">{renderDigestMarkdown(read.reason)}</div>
                        </ArticleLink>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Saved Highlights Section */}
              {digestHighlights.length > 0 && (
                <div className="mt-6 pt-6 border-t border-clay-border">
                  <h4 className="font-display text-md font-semibold text-heading mb-3 flex items-center">
                    <span className="text-lg mr-2">💾</span>
                    Saved Highlights
                    <span className="ml-2 badge-fun-secondary text-xs">
                      {digestHighlights.length}
                    </span>
                  </h4>
                  <div className="space-y-2">
                    {digestHighlights.map((highlight) => (
                      <div
                        key={highlight.id}
                        className="bg-fun-lemon/20 border-l-4 border-fun-lemon px-4 py-3 rounded-r-clay"
                      >
                        <p className="text-heading text-sm leading-relaxed">
                          {highlight.exact_text}
                        </p>
                        <div className="mt-2 flex items-center text-xs text-secondary-text">
                          {highlight.digest_section && (
                            <span className="mr-2">
                              {highlight.digest_section === 'whats_new' && '📰 What\'s New'}
                              {highlight.digest_section === 'core_theme' && '🎯 Core Theme'}
                              {highlight.digest_section === 'recommended_reads' && '⭐ Recommended Reads'}
                            </span>
                          )}
                          <span>•</span>
                          <span className="ml-2">
                            {formatDateCompact(highlight.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicDigestSection;
